import { useQuery, useMutation } from "@tanstack/react-query";
import { useOwnedTalents } from "@/hooks/use-owned-talents";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  Loader2,
  CheckCircle2,
  Link2,
  Mail,
  Unlink,
  Clock,
} from "lucide-react";

type CalendarStatus = {
  talentId: string;
  talentName: string;
  talentEmail: string | null;
  calendarConnected: boolean;
  calendarEmail: string | null;
  pendingInvite: boolean;
  inviteExpiresAt: string | null;
};

export default function TalentCalendarPage() {
  const { toast } = useToast();
  const { ownedTalents, isAdmin, isLoading: talentLoading } = useOwnedTalents();

  const {
    data: statusResp,
    isLoading: statusLoading,
    isError: statusError,
  } = useQuery<{ talent: CalendarStatus[] }>({
    queryKey: ["/api/talent/calendar/status"],
    queryFn: async () => {
      const r = await fetch("/api/talent/calendar/status", {
        credentials: "include",
      });
      if (!r.ok) throw new Error("status unavailable");
      return r.json();
    },
    enabled: ownedTalents.length > 0,
    retry: false,
  });

  const ownedIds = new Set(ownedTalents.map((t) => t.id));
  const statuses = (statusResp?.talent || []).filter((s) =>
    ownedIds.has(s.talentId),
  );

  const invite = useMutation({
    mutationFn: async (talentId: string) => {
      const r = await apiRequest(
        "POST",
        `/api/talent/${talentId}/calendar/invite`,
        {},
      );
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent/calendar/status"],
      });
      toast({
        title: "Invite sent",
        description: "A calendar connection invite was emailed to the talent.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to send invite",
        variant: "destructive",
      }),
  });

  const disconnect = useMutation({
    mutationFn: async (talentId: string) => {
      await apiRequest("POST", `/api/talent/${talentId}/calendar/disconnect`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent/calendar/status"],
      });
      toast({ title: "Disconnected", description: "Calendar disconnected." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to disconnect",
        variant: "destructive",
      }),
  });

  if (talentLoading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="loading-calendar"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Calendar</CardTitle>
            <CardDescription>
              You don&apos;t have a talent profile linked to your account yet.
              Ask an admin to link you to your talent record.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-4xl">
      <div>
        <h1
          className="text-2xl sm:text-3xl font-bold tracking-tight"
          data-testid="text-calendar-title"
        >
          Calendar
        </h1>
        <p className="text-muted-foreground text-sm">
          Connect a Google Calendar per talent so bookings sync automatically.
        </p>
      </div>

      {statusLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : statusError ? (
        <Card>
          <CardHeader>
            <CardTitle>Calendar connection</CardTitle>
            <CardDescription>
              Calendar status is unavailable right now. Try again shortly.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-3">
          {ownedTalents.map((t) => {
            const st = statuses.find((s) => s.talentId === t.id);
            const connected = !!st?.calendarConnected;
            const pending = !!st?.pendingInvite;
            return (
              <Card key={t.id} data-testid={`card-calendar-${t.id}`}>
                <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-6">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <CalendarDays className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{t.name}</p>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        {connected ? (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            Connected
                            {st?.calendarEmail ? ` • ${st.calendarEmail}` : ""}
                          </>
                        ) : pending ? (
                          <>
                            <Clock className="h-3.5 w-3.5 text-amber-500" />
                            Invite pending
                          </>
                        ) : (
                          <>Not connected</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {connected ? (
                      <>
                        <Badge variant="outline" className="text-xs">
                          Synced
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => disconnect.mutate(t.id)}
                          data-testid={`button-disconnect-${t.id}`}
                        >
                          <Unlink className="h-4 w-4 mr-1" />
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <>
                        {isAdmin ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => invite.mutate(t.id)}
                            disabled={invite.isPending}
                            data-testid={`button-invite-${t.id}`}
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            {pending ? "Resend invite" : "Email invite"}
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          asChild
                          data-testid={`button-connect-${t.id}`}
                        >
                          <a href={`/api/talent/${t.id}/calendar/auth`}>
                            <Link2 className="h-4 w-4 mr-1" />
                            Connect
                          </a>
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
